import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, RefreshControl, TouchableOpacity } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { useFocusEffect } from '@react-navigation/native';
import { addDays, format, parseISO } from 'date-fns';
import Icon from 'react-native-vector-icons/Ionicons';

import { RootState, AppDispatch } from '@/store';
import { getPayPeriodSummary, getPunchHistory } from '@/store/slices/timeClockSlice';
import { TimeEntryResponse } from '@/types/api';
import { colors, font, space, radius, shadow } from '@/theme/chalkCourt';
import LoadingScreen from '@/components/common/LoadingScreen';
import AddMissingPunchModal, { MissingPunchTarget } from '@/components/AddMissingPunchModal';
import SwitchPunchModal, { SwitchTarget } from '@/components/SwitchPunchModal';
import DoublePunchNotice from '@/components/DoublePunchNotice';
import { doublePunchIds, findDoublePunches } from '@/utils/punchAnomalies';

const fmtDuration = (minutes: number): string => {
  const m = Math.max(0, Math.round(minutes));
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return h === 0 ? `${rem}m` : `${h}h ${rem.toString().padStart(2, '0')}m`;
};

const safeParse = (iso?: string): Date | null => {
  if (!iso) return null;
  try {
    const d = parseISO(iso);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
};

const fmtTime = (iso?: string): string | null => {
  const d = safeParse(iso);
  return d ? format(d, 'h:mm a') : null;
};

const fmtRangeDate = (iso?: string): string => {
  const d = safeParse(iso);
  return d ? format(d, 'MMM d') : '—';
};

const fmtDayHeader = (iso: string): string => {
  const d = safeParse(iso);
  return d ? format(d, 'EEE · MMM d') : iso;
};

/**
 * The range to request from `/timesheet/range`, clamped so the endDate never
 * exceeds today (the endpoint 400s on future dates and future days have no
 * punches). Returns null until the pay-period bounds are known.
 */
const rangeFor = (
  summary?: { startDate?: string; endDate?: string } | null
): { startDate: string; endDate: string } | null => {
  if (!summary?.startDate || !summary?.endDate) return null;
  const today = format(new Date(), 'yyyy-MM-dd');
  return {
    startDate: summary.startDate,
    endDate: summary.endDate > today ? today : summary.endDate,
  };
};

/** "2026-10-09T17:05:00" → "17:05" (the HH:mm of an ISO local timestamp). */
const hhmm = (iso?: string | null): string | null => {
  const d = safeParse(iso ?? undefined);
  return d ? format(d, 'HH:mm') : null;
};

/** Pay-period days a punch can be added to: start → min(end, today), newest first. */
const addableDays = (summary?: { startDate?: string; endDate?: string } | null): string[] => {
  const range = rangeFor(summary);
  if (!range) return [format(new Date(), 'yyyy-MM-dd')];
  const out: string[] = [];
  for (let d = parseISO(range.startDate); format(d, 'yyyy-MM-dd') <= range.endDate; d = addDays(d, 1)) {
    out.push(format(d, 'yyyy-MM-dd'));
  }
  return out.reverse();
};

interface DayGroup {
  date: string;
  entries: TimeEntryResponse[];
  totalMinutes: number;
}

/**
 * Timesheet — the staff member's clock history for the current pay period.
 * Sources: `/clock/pay-period` (range + total) and `/timesheet/range` (the
 * per-entry punches, stored in `punchHistory`). Durations/times prefer the
 * ADJUSTED values — the single source of truth for pay (see time-clock rules).
 */
const TimesheetScreen: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { payPeriodSummary, punchHistory, isLoadingPayPeriod } = useSelector(
    (state: RootState) => state.timeClock
  );

  // Refetch every time the tab gains focus (not just on mount) — React
  // Navigation keeps tab screens mounted, so a mount-only fetch goes stale
  // after you clock out and return to this tab. On first focus the summary is
  // null (the range effect below then loads punches); on later focuses we also
  // re-pull punches directly since the range is unchanged.
  useFocusEffect(
    useCallback(() => {
      dispatch(getPayPeriodSummary());
      const range = rangeFor(payPeriodSummary);
      if (range) dispatch(getPunchHistory(range));
    }, [dispatch, payPeriodSummary?.startDate, payPeriodSummary?.endDate])
  );

  // Once we know the pay-period bounds, pull the punch entries for that range.
  // The pay period can extend into the future (e.g. a 2-week period), but
  // `/timesheet/range` rejects an endDate past tomorrow — and future days have
  // no punches anyway — so clamp the requested end to today.
  useEffect(() => {
    const range = rangeFor(payPeriodSummary);
    if (range) dispatch(getPunchHistory(range));
  }, [dispatch, payPeriodSummary?.startDate, payPeriodSummary?.endDate]);

  // Pull-to-refresh: re-pull BOTH the summary and the punch entries. (The
  // punch effect above only re-runs when the range changes, so mid-period a
  // summary-only refresh would miss newly-added punches.) Track a dedicated
  // flag: `isLoadingPayPeriod` only covers the summary, so the spinner would
  // drop while the punch-history fetch was still running.
  const [refreshing, setRefreshing] = useState(false);
  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const range = rangeFor(payPeriodSummary);
      await Promise.all([
        dispatch(getPayPeriodSummary()),
        ...(range ? [dispatch(getPunchHistory(range))] : []),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, payPeriodSummary?.startDate, payPeriodSummary?.endDate]);

  // Group by day, newest first. Anchor on clock-IN punches (each carries its
  // pair's duration + paired clock-out time). Skip only PAIRED clock-outs — the
  // clock-in half already renders the pair, so keeping them would double-count.
  // Orphan clock-outs (no pair: admin-added, or the clock-in fell outside this
  // range) are KEPT and flagged so nothing is silently hidden (#31).
  const days = useMemo<DayGroup[]>(() => {
    const map = new Map<string, DayGroup>();
    for (const entry of punchHistory) {
      if (!entry.clockIn && entry.pairedPunchId) continue;
      const group =
        map.get(entry.punchDate) ?? { date: entry.punchDate, entries: [], totalMinutes: 0 };
      group.entries.push(entry);
      // Only clock-ins carry a pair duration; an orphan clock-out contributes 0.
      group.totalMinutes += entry.clockIn ? entry.durationMinutes ?? 0 : 0;
      map.set(entry.punchDate, group);
    }
    // Order each day's rows by recorded (adjusted) time so an orphan clock-out
    // sits where it happened, not appended after the paired rows.
    for (const g of map.values()) {
      g.entries.sort((a, b) => (a.adjustedTime ?? '').localeCompare(b.adjustedTime ?? ''));
    }
    return Array.from(map.values()).sort((a, b) => b.date.localeCompare(a.date));
  }, [punchHistory]);

  const todayIso = format(new Date(), 'yyyy-MM-dd');

  // Two clock-ins / clock-outs in a row, per day — fixed by a switch or an added punch.
  const doubles = useMemo(() => findDoublePunches(punchHistory), [punchHistory]);
  const inDouble = useMemo(() => doublePunchIds(doubles), [doubles]);
  const [switchTarget, setSwitchTarget] = useState<SwitchTarget | null>(null);

  // Missing-punch sheet: open for a specific gap (target) or the general form (null target).
  const [punchSheet, setPunchSheet] = useState<{ target: MissingPunchTarget | null } | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  useEffect(() => {
    if (!sent) return undefined;
    const id = setTimeout(() => setSent(null), 5000);
    return () => clearTimeout(id);
  }, [sent]);
  const onPunchSent = (message: string) => {
    setSent(message);
    onRefresh();
  };

  if (isLoadingPayPeriod && !payPeriodSummary) {
    return <LoadingScreen message="Loading your timesheet…" />;
  }

  const totalHours = payPeriodSummary ? fmtDuration(payPeriodSummary.totalMinutesWorked) : '—';
  const rangeLabel = payPeriodSummary
    ? `${fmtRangeDate(payPeriodSummary.startDate)} – ${fmtRangeDate(payPeriodSummary.endDate)}`
    : '—';
  const punchCount = payPeriodSummary?.punchCount ?? 0;

  return (
    <ScrollView
      style={styles.screen}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={refreshing}
          onRefresh={onRefresh}
          tintColor={colors.accent}
          colors={[colors.accent]}
        />
      }>
      {/* Header — pay period total */}
      <View style={styles.header}>
        <Text style={styles.kicker}>PAY PERIOD</Text>
        <Text style={styles.headline}>TIMESHEET</Text>
        <View style={styles.rule} />
        <View style={styles.totalRow}>
          <Text style={styles.totalValue}>{totalHours}</Text>
          <View style={styles.totalMeta}>
            <Text style={styles.totalLabel}>TOTAL HOURS</Text>
            <Text style={styles.rangeLabel}>{rangeLabel}</Text>
          </View>
        </View>
        <Text style={styles.metaLine}>
          {punchCount} punch{punchCount === 1 ? '' : 'es'} this period
        </Text>
        <TouchableOpacity
          style={styles.addBtn}
          activeOpacity={0.8}
          onPress={() => setPunchSheet({ target: null })}>
          <Icon name="add-circle-outline" size={18} color={colors.accent} />
          <Text style={styles.addBtnText}>Add a missing punch</Text>
        </TouchableOpacity>
      </View>

      {sent && (
        <View style={styles.sentBanner}>
          <Icon name="checkmark-circle" size={18} color={colors.court} />
          <Text style={styles.sentText}>{sent}</Text>
        </View>
      )}

      {/* Daily breakdown */}
      {days.length === 0 ? (
        <View style={styles.empty}>
          <View style={styles.emptyMark}>
            <Icon name="time-outline" size={30} color={colors.inkFaint} />
          </View>
          <Text style={styles.emptyTitle}>No punches yet</Text>
          <Text style={styles.emptyBody}>
            Your clock in / out history for this pay period will show up here.
          </Text>
        </View>
      ) : (
        days.map((day) => (
          <View key={day.date} style={styles.dayCard}>
            <View style={styles.dayHead}>
              <Text style={styles.dayDate}>{fmtDayHeader(day.date)}</Text>
              <Text style={styles.dayTotal}>{fmtDuration(day.totalMinutes)}</Text>
            </View>
            {doubles
              .filter((d) => d.date === day.date)
              .map((d) => (
                <DoublePunchNotice
                  key={`double-${d.second.id}`}
                  double={d}
                  onSwitch={setSwitchTarget}
                  onAddMissing={(target) => setPunchSheet({ target })}
                />
              ))}
            {day.entries.map((entry) => {
              const ownTime = entry.displayTime || fmtTime(entry.adjustedTime);
              const paired = !!entry.pairedPunchId;
              const mobile = /mobile|nfc/i.test(entry.note || '');

              // Three shapes: a paired clock-in (in → out), an unpaired clock-out
              // (missing clock-in), or an open clock-in ("on the clock" today, or a
              // missed clock-out on a past day). The last two are unpaired (#31).
              const orphanOut = !entry.clockIn; // only orphan clock-outs survive grouping
              const openInStale = entry.clockIn && !paired && day.date < todayIso;
              // An open clock-in TODAY is the normal "on the clock" state, not an
              // anomaly — only orphan clock-outs and stale open clock-ins get the ⚠.
              const unpaired = orphanOut || openInStale;

              const leftText = orphanOut ? 'Missing in' : ownTime ?? '—';
              const rightText = orphanOut
                ? ownTime ?? '—'
                : paired
                  ? fmtTime(entry.pairedTime || undefined) ?? '—'
                  : openInStale
                    ? 'Missing out'
                    : 'On the clock';

              // The gap's known half, for the add sheet (orphan out → add the in; stale in → add the out).
              const known = hhmm(entry.adjustedTime);
              // A punch in a double is handled by the notice above (switch or add between).
              const fix: MissingPunchTarget | null =
                unpaired && known && !inDouble.has(entry.id)
                  ? { date: day.date, mode: orphanOut ? 'in' : 'out', otherTime: known }
                  : null;

              return (
                <View key={entry.id}>
                <View style={styles.entryRow}>
                  <View style={styles.entryTimes}>
                    <Text style={[styles.entryTime, orphanOut && styles.entryWarnText]}>
                      {leftText}
                    </Text>
                    <Icon name="arrow-forward" size={13} color={colors.inkFaint} />
                    <Text
                      style={[
                        styles.entryTime,
                        !paired && !orphanOut && styles.entryOpen,
                        openInStale && styles.entryWarnText,
                      ]}>
                      {rightText}
                    </Text>
                  </View>
                  <View style={styles.entryMeta}>
                    {mobile ? (
                      <Icon name="phone-portrait-outline" size={13} color={colors.inkFaint} />
                    ) : null}
                    {unpaired ? (
                      <Icon name="warning-outline" size={14} color={colors.warn} />
                    ) : null}
                    <Text style={[styles.entryDur, unpaired && styles.entryWarnText]}>
                      {paired ? fmtDuration(entry.durationMinutes ?? 0) : unpaired ? 'Unpaired' : '—'}
                    </Text>
                  </View>
                </View>
                {fix && (
                  <TouchableOpacity
                    style={styles.fixBtn}
                    activeOpacity={0.85}
                    onPress={() => setPunchSheet({ target: fix })}>
                    <Icon name="add-circle-outline" size={16} color={colors.accent} />
                    <Text style={styles.fixBtnText}>
                      {fix.mode === 'out' ? 'Add the missing clock-out' : 'Add the missing clock-in'}
                    </Text>
                  </TouchableOpacity>
                )}
                </View>
              );
            })}
          </View>
        ))
      )}

      <Text style={styles.footnote}>
        Times shown are your recorded clock in / out. A ⚠ marks an unpaired punch — a clock-out
        with no matching clock-in, or a clock-in with no clock-out. Add the missing punch to fix it.
      </Text>

      <SwitchPunchModal
        target={switchTarget}
        onClose={() => setSwitchTarget(null)}
        onSwitched={onPunchSent}
      />
      <AddMissingPunchModal
        visible={!!punchSheet}
        target={punchSheet?.target}
        days={addableDays(payPeriodSummary)}
        onClose={() => setPunchSheet(null)}
        onSubmitted={onPunchSent}
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  content: {
    padding: space.lg,
    paddingBottom: space.xxl,
  },

  header: {
    marginBottom: space.lg,
  },
  kicker: {
    fontFamily: font.displayMed,
    fontSize: 13,
    letterSpacing: 1.5,
    color: colors.inkFaint,
  },
  headline: {
    fontFamily: font.display,
    fontSize: 40,
    lineHeight: 44,
    color: colors.ink,
  },
  rule: {
    height: 2,
    backgroundColor: colors.lineStrong,
    width: 44,
    marginVertical: space.md,
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: space.md,
  },
  totalValue: {
    fontFamily: font.display,
    fontSize: 44,
    lineHeight: 46,
    color: colors.accent,
  },
  totalMeta: {
    paddingBottom: 4,
  },
  totalLabel: {
    fontFamily: font.bodySemi,
    fontSize: 12,
    letterSpacing: 1,
    color: colors.inkSoft,
  },
  rangeLabel: {
    fontFamily: font.body,
    fontSize: 13,
    color: colors.inkFaint,
  },
  metaLine: {
    fontFamily: font.body,
    fontSize: 13,
    color: colors.inkFaint,
    marginTop: space.sm,
  },

  dayCard: {
    backgroundColor: colors.surface,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.md,
    borderWidth: 1,
    borderColor: colors.line,
    ...shadow.card,
  },
  dayHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: space.sm,
  },
  dayDate: {
    fontFamily: font.bodyBold,
    fontSize: 15,
    color: colors.ink,
  },
  dayTotal: {
    fontFamily: font.displayMed,
    fontSize: 16,
    color: colors.court,
  },
  entryRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderTopWidth: 1,
    borderTopColor: colors.line,
  },
  entryTimes: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
  },
  entryTime: {
    fontFamily: font.bodyMed,
    fontSize: 14,
    color: colors.ink,
  },
  entryOpen: {
    color: colors.accent,
    fontFamily: font.bodySemi,
  },
  entryWarnText: {
    color: colors.warn,
    fontFamily: font.bodySemi,
  },
  entryMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  entryDur: {
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: colors.inkSoft,
    minWidth: 54,
    textAlign: 'right',
  },

  addBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 6,
    marginTop: space.md,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: radius.pill,
    borderWidth: 1,
    borderColor: colors.accent,
    backgroundColor: colors.surface,
  },
  addBtnText: {
    fontFamily: font.bodySemi,
    fontSize: 14,
    color: colors.accent,
  },
  sentBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.courtSoft,
    borderRadius: radius.md,
    padding: space.md,
    marginBottom: space.md,
  },
  sentText: {
    flex: 1,
    fontFamily: font.bodySemi,
    fontSize: 13.5,
    color: colors.court,
  },
  fixBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    marginBottom: 8,
    paddingVertical: 8,
    borderRadius: radius.sm,
    borderWidth: 1,
    borderColor: colors.accent,
    backgroundColor: colors.accentSoft,
  },
  fixBtnText: {
    fontFamily: font.bodySemi,
    fontSize: 13,
    color: colors.accentDark,
  },

  empty: {
    alignItems: 'center',
    paddingVertical: space.xxl,
  },
  emptyMark: {
    width: 60,
    height: 60,
    borderRadius: radius.pill,
    backgroundColor: colors.surfaceSunk,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: space.md,
  },
  emptyTitle: {
    fontFamily: font.bodyBold,
    fontSize: 16,
    color: colors.ink,
    marginBottom: 4,
  },
  emptyBody: {
    fontFamily: font.body,
    fontSize: 14,
    color: colors.inkFaint,
    textAlign: 'center',
    paddingHorizontal: space.xl,
  },

  footnote: {
    fontFamily: font.body,
    fontSize: 12,
    color: colors.inkFaint,
    textAlign: 'center',
    marginTop: space.md,
  },
});

export default TimesheetScreen;
